import { useEffect, useRef, useState, type CSSProperties } from "react";
import { fechaEnTexto as aTexto, fechaDeTexto as textoAIso, formatearFechaEscrita as formatear } from "../../../dominio/src/index.ts";

/**
 * Campo de fecha que siempre se ve y se escribe como dd/mm/aaaa, sin importar el idioma del navegador ni del sistema
 * (el campo de fecha nativo muestra mm/dd/aaaa en equipos configurados en inglés de Estados Unidos).
 * Por dentro la fecha sigue siendo aaaa-mm-dd, como la usa el resto de Donna. El botón del calendario abre el selector nativo.
 */
const CALENDARIO = (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>
);

export function FechaInput({ value, onChange, min, max, disabled, required, className, style, id, ...resto }: {
  value: string; onChange: (e: { target: { value: string } }) => void; min?: string; max?: string; disabled?: boolean; required?: boolean;
  className?: string; style?: CSSProperties; id?: string; "aria-label"?: string;
}) {
  const [texto, setTexto] = useState(aTexto(value));
  const nativo = useRef<HTMLInputElement>(null);
  // si la fecha cambia desde afuera (por ejemplo, al elegir otra cita), el texto la sigue
  useEffect(() => { if (textoAIso(texto) !== (value || null) && !(value === "" && texto.length < 10)) setTexto(aTexto(value)); }, [value]);
  const fuera = (iso: string) => (!!min && iso < min) || (!!max && iso > max);
  const iso = textoAIso(texto);
  const invalida = texto.length === 10 && (iso === null || fuera(iso));

  function escribir(t: string) {
    const f = formatear(t);
    setTexto(f);
    if (f === "") { onChange({ target: { value: "" } }); return; }
    const v = textoAIso(f);
    if (v !== null && !fuera(v)) onChange({ target: { value: v } });
  }
  function abrirCalendario() {
    const el = nativo.current;
    if (!el || disabled) return;
    try { if (typeof el.showPicker === "function") { el.showPicker(); return; } } catch { /* el navegador no deja abrirlo: se intenta con el clic */ }
    el.focus(); el.click();
  }
  const dd = (d: string) => aTexto(d);
  return (
    <span className="campo-fecha" style={style}>
      <input id={id} className={className} type="text" inputMode="numeric" autoComplete="off" placeholder="dd/mm/aaaa" maxLength={10} value={texto} disabled={disabled} required={required}
        aria-invalid={invalida || undefined} title={invalida ? `Escribe una fecha real${min ? ` desde el ${dd(min)}` : ""}${max ? ` hasta el ${dd(max)}` : ""}, como dd/mm/aaaa.` : undefined}
        aria-label={resto["aria-label"]} onChange={(e) => escribir(e.target.value)}
        onBlur={() => { if (texto.length > 0 && texto.length < 10) { setTexto(aTexto(value)); } else if (invalida) setTexto(aTexto(value)); }} />
      {/* en iPhone y iPad el selector solo se abre si la persona toca el propio campo de fecha (Safari ignora el que se abre por programa):
          por eso, en pantallas táctiles, el campo nativo transparente queda ENCIMA del botón del calendario (ver .cal-envoltorio en estilos.css) */}
      <span className="cal-envoltorio">
        <button type="button" className="btn-calendario" onClick={abrirCalendario} disabled={disabled} aria-label="Abrir el calendario" title="Abrir el calendario">{CALENDARIO}</button>
        <input ref={nativo} type="date" className="fecha-nativa" tabIndex={-1} aria-label="Elegir la fecha en el calendario" value={value} min={min} max={max} disabled={disabled}
          onChange={(e) => { if (e.target.value) { setTexto(aTexto(e.target.value)); onChange({ target: { value: e.target.value } }); } }} />
      </span>
    </span>
  );
}
